import Phaser from 'phaser';
import { Bey } from '../entities/Bey.ts';
import { getBeyDef } from '../game/stats.ts';
import { specialFor } from '../game/specials.ts';
import type { GameMode } from '../game/tournament.ts';
import { resolveClash, isBurst, type ClashSide } from '../game/combat.ts';
import { SIM } from '../game/config.ts';
import { resolveBoundary, type ArenaConfig } from '../game/arena.ts';
import type { WorldSnapshot, Controller, Command } from '../game/command.ts';
import { NO_COMMAND } from '../game/command.ts';
import { PlayerController, KEYS_P1, KEYS_P2 } from '../input/PlayerController.ts';
import { AiController } from '../game/ai.ts';
import { Hud } from '../ui/Hud.ts';
import { LaunchMeter } from '../ui/LaunchMeter.ts';
import { TouchControls, touchEnabled } from '../ui/TouchControls.ts';
import { launchPower, powerToSpin, powerToSpeedMult, aiLaunchPower, isPerfect } from '../game/launch.ts';
import { sound } from '../audio/sound.ts';
import { getArena } from '../arenas.ts';
import { WORLD, PHYS } from '../world.ts';
import { sub, len, normalize, scale } from '../game/vec.ts';

type Phase = 'countdown' | 'fight' | 'over';
export type FinishType = 'spinout' | 'ringout' | 'burst';

/** Launch power used if the meter is somehow never engaged (a decent default). */
const LAUNCH_FALLBACK = 0.85;

/** Combine keyboard + touch commands so either input drives the player. */
function mergeCommands(a: Command, b: Command): Command {
  const aSteer = Math.hypot(a.steer.x, a.steer.y) > 0.01;
  return {
    steer: aSteer ? a.steer : b.steer,
    dash: a.dash || b.dash,
    extremeDash: a.extremeDash || b.extremeDash,
    holdCenter: a.holdCenter || b.holdCenter,
    special: a.special || b.special,
  };
}

interface BattleData {
  playerId: string;
  opponentId: string;
  difficulty: number;
  mode: GameMode;
}

export class BattleScene extends Phaser.Scene {
  private player!: Bey;
  private opponent!: Bey;
  private playerCtl!: Controller;
  private aiCtl!: Controller;
  private hud!: Hud;
  private launchMeter!: LaunchMeter;
  private touch?: TouchControls;
  private arenaCfg!: ArenaConfig;
  private phase: Phase = 'countdown';
  private match!: BattleData;
  private difficulty = 0.7;
  private versus = false;
  private playerLaunchPower = NaN;
  private paused = false;
  private pauseText!: Phaser.GameObjects.Text;
  private pauseBg!: Phaser.GameObjects.Rectangle;
  private sparks!: Phaser.GameObjects.Particles.ParticleEmitter;

  /** Match (best-of) state. */
  private playerScore = 0;
  private oppScore = 0;
  private roundNum = 1;
  private startSpin = NaN;
  private rim: number = WORLD.arena.rim;
  /** Round wins needed to take the match (best of 3). ?wins=N overrides (debug). */
  private winsNeeded = 2;

  constructor() {
    super('Battle');
  }

  create(data: BattleData): void {
    this.match = data;
    this.phase = 'countdown';
    this.playerScore = 0;
    this.oppScore = 0;
    this.roundNum = 1;
    const winsParam = parseInt(new URLSearchParams(location.search).get('wins') ?? '', 10);
    this.winsNeeded = Number.isNaN(winsParam) ? 2 : Math.max(1, winsParam);
    this.cameras.main.setBackgroundColor('#05060a');

    const { cx, cy } = WORLD.arena;
    const arena = getArena((this.registry.get('arena') as string) ?? 'lab');
    const rim = arena.rim;
    this.rim = rim;

    // Solid base so nothing ever shows through, then the tech-lab background
    // (center-cropped to the canvas, tinted per arena) with a darkening overlay
    // so the beys and HUD read clearly on top.
    this.add
      .rectangle(WORLD.width / 2, WORLD.height / 2, WORLD.width, WORLD.height, 0x05060a)
      .setDepth(-3);
    this.add.image(WORLD.width / 2, WORLD.height / 2, 'arena-bg').setTint(arena.bgTint).setDepth(-2);
    this.add
      .rectangle(WORLD.width / 2, WORLD.height / 2, WORLD.width, WORLD.height, 0x05060a, 0.4)
      .setDepth(-1);

    // The dish, scaled so the playable floor matches this arena's ring-out radius.
    const dishSize = rim * WORLD.arena.dishScale;
    this.add
      .image(cx, cy, 'arena-dish')
      .setDisplaySize(dishSize, dishSize)
      .setTint(arena.dishTint)
      .setDepth(0);

    this.arenaCfg = {
      center: { x: cx, y: cy },
      rimRadius: rim,
      pullK: arena.pull,
      ringOutSpeed: arena.ringOutSpeed,
      bounceRestitution: PHYS.bounceRestitution,
    };

    const beyArena = { rim, pull: arena.pull };
    const playerDef = getBeyDef(data.playerId);
    const oppDef = getBeyDef(data.opponentId);
    this.player = new Bey(this, playerDef, cx - rim * 0.5, cy + rim * 0.3, beyArena);
    this.opponent = new Bey(this, oppDef, cx + rim * 0.5, cy - rim * 0.3, beyArena);
    this.player.sprite.setDepth(10);
    this.opponent.sprite.setDepth(10);

    // Debug aid: ?spin=N starts both beys at N spin every round (used by the
    // smoke test to force quick finishes). Harmless/ignored in normal play.
    this.startSpin = parseFloat(new URLSearchParams(location.search).get('spin') ?? '');
    this.applyStartSpin();

    this.difficulty = data.difficulty ?? 0.7;
    this.versus = data.mode === 'versus';
    this.playerCtl = new PlayerController(this, KEYS_P1);
    // In versus, the "opponent" is a second human; otherwise it's the AI.
    this.aiCtl = this.versus
      ? new PlayerController(this, KEYS_P2)
      : new AiController({ difficulty: this.difficulty, type: oppDef.type });

    // FX: clash sparks (burst on demand) + per-bey speed trails.
    this.sparks = this.add
      .particles(0, 0, 'spark', {
        lifespan: 320,
        speed: { min: 60, max: 260 },
        scale: { start: 0.9, end: 0 },
        blendMode: 'ADD',
        emitting: false,
      })
      .setDepth(20);
    for (const b of [this.player, this.opponent]) {
      this.add
        .particles(0, 0, 'glow', {
          lifespan: 260,
          scale: { start: 0.5, end: 0 },
          alpha: { start: 0.5, end: 0 },
          tint: b.def.color,
          frequency: 35,
          blendMode: 'ADD',
        })
        .setDepth(5)
        .startFollow(b.sprite);
    }

    this.hud = new Hud(this);
    this.hud.setBeys(this.player, this.opponent);
    this.hud.setScore(0, 0, this.winsNeeded);

    this.launchMeter = new LaunchMeter(this);
    this.touch = touchEnabled() ? new TouchControls(this) : undefined;

    // Keyboard players get a compact controls reference in the corner (touch
    // players already have labeled on-screen buttons).
    if (!this.touch) {
      this.add
        .text(
          14,
          WORLD.height - 12,
          'Arrows  move\nSpace  strike     Shift  extreme dash\nC  hold center     E  special     P  pause',
          {
            fontFamily: 'Trebuchet MS, sans-serif',
            fontSize: '13px',
            color: '#8aa0c8',
            lineSpacing: 3,
          },
        )
        .setOrigin(0, 1)
        .setDepth(55)
        .setAlpha(0.7);
    }
    // Space locks the launch power during the countdown (it's the dash key
    // during the fight, but this only acts while the meter is sweeping).
    this.input.keyboard!.on('keydown-SPACE', () => {
      if (this.phase === 'countdown' && this.launchMeter.active) this.lockPlayerLaunch();
    });

    // Pause / quit (fight only).
    this.paused = false;
    this.pauseBg = this.add
      .rectangle(WORLD.width / 2, WORLD.height / 2, WORLD.width, WORLD.height, 0x05060a, 0.7)
      .setDepth(70)
      .setVisible(false);
    this.pauseText = this.add
      .text(WORLD.width / 2, WORLD.height / 2, 'PAUSED\n\nP / Esc — resume\nQ — quit to title', {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '40px',
        fontStyle: 'bold',
        color: '#ffffff',
        align: 'center',
        stroke: '#000',
        strokeThickness: 6,
      })
      .setOrigin(0.5)
      .setDepth(71)
      .setVisible(false);
    this.input.keyboard!.on('keydown-P', () => this.togglePause());
    this.input.keyboard!.on('keydown-ESC', () => this.togglePause());
    this.input.keyboard!.on('keydown-Q', () => {
      if (this.paused) {
        this.matter.world.engine.timing.timeScale = 1;
        sound.stopWhir();
        this.scene.start('Title');
      }
    });

    // Safety net: never leave the ambient whir running past this scene.
    this.events.once('shutdown', () => sound.stopWhir());

    this.matter.world.on('collisionstart', this.onCollision, this);

    this.runCountdown();
  }

  private runCountdown(): void {
    this.playerLaunchPower = NaN;
    // In versus there's only one keyboard, so skip the per-player launch meter
    // and auto-launch both at a fixed power.
    if (!this.versus) this.launchMeter.begin();
    const steps = ['3', '2', '1', 'LET IT RIP!'];
    steps.forEach((s, i) => {
      this.time.delayedCall(i * 700, () => {
        const color = i === steps.length - 1 ? '#ffd23b' : '#ffffff';
        this.hud.setStatus(s, color);
        this.cameras.main.flash(120, 255, 255, 255, false);
        sound.countdown(i);
      });
    });
    this.time.delayedCall(steps.length * 700, () => this.beginFight());
  }

  /** Lock the player's launch power from the meter (mid-countdown tap). */
  private lockPlayerLaunch(): void {
    const pos = this.launchMeter.lock();
    this.playerLaunchPower = launchPower(pos);
    if (isPerfect(pos)) this.hud.flashSpecial(true, 'Perfect Launch');
  }

  /** End the countdown: lock any un-locked launch, then launch both beys. */
  private beginFight(): void {
    if (this.launchMeter.active) this.lockPlayerLaunch();
    this.launchMeter.hide();
    this.phase = 'fight';
    this.hud.setStatus('');

    const pPow = Number.isNaN(this.playerLaunchPower) ? LAUNCH_FALLBACK : this.playerLaunchPower;
    // The opponent launches by AI difficulty, or at the fixed default in versus.
    const aPow = this.versus ? LAUNCH_FALLBACK : aiLaunchPower(this.difficulty);
    this.player.spin = powerToSpin(pPow);
    this.opponent.spin = powerToSpin(aPow);
    this.player.launchOrbit(1, powerToSpeedMult(pPow));
    this.opponent.launchOrbit(-1, powerToSpeedMult(aPow));
    sound.launch(pPow);
    sound.startWhir();

    // Debug ?spin overrides launch-set spin so the smoke test can force finishes.
    this.applyStartSpin();
  }

  private togglePause(): void {
    // Only pause an active fight (not the countdown or the finish flourish).
    if (this.phase !== 'fight' && !this.paused) return;
    this.paused = !this.paused;
    this.matter.world.engine.timing.timeScale = this.paused ? 0 : 1;
    this.pauseBg.setVisible(this.paused);
    this.pauseText.setVisible(this.paused);
  }

  update(_t: number, delta: number): void {
    if (this.paused) return;
    const dt = Math.min(delta / 1000, 0.05);

    if (this.phase === 'countdown') {
      this.launchMeter.update(dt);
      // A tap of the touch STRIKE button locks the launch, like Space.
      if (this.touch && this.launchMeter.active && this.touch.command().dash) {
        this.lockPlayerLaunch();
      }
    }

    if (this.phase === 'fight') {
      const wPlayer = this.makeSnapshot(this.player, this.opponent);
      const wOpp = this.makeSnapshot(this.opponent, this.player);
      let cmdP = this.guardCmd(this.playerCtl.decide(wPlayer, dt));
      if (this.touch) cmdP = mergeCommands(cmdP, this.touch.command());
      const cmdA = this.guardCmd(this.aiCtl.decide(wOpp, dt));
      this.player.preStep(dt, cmdP, this.opponent.pos);
      this.opponent.preStep(dt, cmdA, this.player.pos);

      this.resolveBounds(this.player);
      this.resolveBounds(this.opponent);
      this.actionFx(this.player);
      this.actionFx(this.opponent);
      // Ambient whir tracks the combined spin energy of both beys.
      sound.setWhir((this.player.spin + this.opponent.spin) / (2 * SIM.MAX_SPIN));
      this.checkWin();
    }

    this.player.render(dt);
    this.opponent.render(dt);
    this.applyChargeGlow(this.player);
    this.applyChargeGlow(this.opponent);
    this.hud.update(this.player, this.opponent);
  }

  /** Spawn launch FX when a bey fires a strike or extreme dash this frame. */
  private actionFx(bey: Bey): void {
    if (bey.firedDash) {
      this.sparks.emitParticleAt(bey.pos.x, bey.pos.y, 10);
      this.impactFlash(bey.pos.x, bey.pos.y, 1.4, bey.def.color);
      this.cameras.main.shake(80, 0.004);
    }
    if (bey.firedExtreme) {
      this.impactFlash(bey.pos.x, bey.pos.y, 2.6, bey.def.accent);
      this.cameras.main.flash(140, 255, 240, 180);
      this.cameras.main.shake(160, 0.007);
    }
    if (bey.firedSpecial) {
      this.specialFx(bey, bey.firedSpecial);
      this.hud.flashSpecial(bey === this.player, specialFor(bey.def.type).name);
      sound.special();
    }
  }

  /** Distinct signature VFX per special kind. */
  private specialFx(bey: Bey, kind: 'rush' | 'counter' | 'secondWind' | 'focus'): void {
    const { x, y } = bey.pos;
    this.cameras.main.flash(160, 255, 255, 255);
    switch (kind) {
      case 'rush': // fiery burst that trails the homing dash
        this.impactFlash(x, y, 3.8, 0xff5a2a);
        this.impactFlash(x, y, 2.4, 0xffd23b);
        this.sparks.emitParticleAt(x, y, 34);
        this.cameras.main.shake(220, 0.01);
        break;
      case 'counter': // cyan shield rings snapping up
        this.impactFlash(x, y, 3.4, 0x5ad6ff);
        this.impactFlash(x, y, 2.2, 0xffffff);
        this.time.delayedCall(120, () => this.impactFlash(bey.pos.x, bey.pos.y, 4.0, 0x5ad6ff));
        break;
      case 'secondWind': // green healing surge
        this.impactFlash(x, y, 3.6, 0x2fd27a);
        this.impactFlash(x, y, 2.2, 0xd2f7e0);
        this.sparks.emitParticleAt(x, y, 24);
        break;
      case 'focus': // bright all-round aura
        this.impactFlash(x, y, 3.4, bey.def.accent);
        this.impactFlash(x, y, 2.2, 0xffffff);
        this.cameras.main.shake(120, 0.005);
        break;
    }
  }

  /** A quick expanding glow ring for impacts and dash launches. */
  private impactFlash(x: number, y: number, scale: number, tint: number): void {
    const f = this.add.image(x, y, 'glow').setTint(tint).setBlendMode(Phaser.BlendModes.ADD).setDepth(19);
    this.tweens.add({
      targets: f,
      scale: { from: scale * 0.4, to: scale },
      alpha: { from: 0.9, to: 0 },
      duration: 300,
      onComplete: () => f.destroy(),
    });
  }

  private guardCmd(cmd: Command): Command {
    return cmd ?? NO_COMMAND;
  }

  private makeSnapshot(self: Bey, opp: Bey): WorldSnapshot {
    return {
      self: self.snapshot(),
      opponent: opp.snapshot(),
      arenaCenter: this.arenaCfg.center,
      rimRadius: this.arenaCfg.rimRadius,
    };
  }

  private resolveBounds(bey: Bey): void {
    if (this.phase !== 'fight') return;
    const res = resolveBoundary(bey.pos, bey.velocity, this.arenaCfg, bey.canRingOut);
    if (res.ringOut) {
      bey.alive = false;
      this.finish(bey, 'ringout');
      return;
    }
    if (res.bounced) {
      bey.sprite.setPosition(res.pos.x, res.pos.y);
      bey.sprite.setVelocity(res.vel.x, res.vel.y);
    }
  }

  private checkWin(): void {
    if (this.phase !== 'fight') return;
    // Burst takes priority (most dramatic), then spin-out.
    for (const bey of [this.player, this.opponent]) {
      if (isBurst(bey.burst)) return this.finish(bey, 'burst');
    }
    for (const bey of [this.player, this.opponent]) {
      if (bey.spin <= 0) return this.finish(bey, 'spinout');
    }
  }

  private finish(loser: Bey, finish: FinishType): void {
    if (this.phase === 'over') return;
    this.phase = 'over';
    const playerWon = loser === this.opponent;

    if (playerWon) this.playerScore++;
    else this.oppScore++;
    this.hud.setScore(this.playerScore, this.oppScore, this.winsNeeded);

    sound.finish(finish);
    sound.stopWhir();

    // Cinematic flourish: drop into slow-motion so the deciding moment lands,
    // and flash. The loser keeps flying / sputtering in slow-mo (no fight logic
    // runs during 'over', so it coasts).
    this.matter.world.engine.timing.timeScale = finish === 'burst' ? 0.14 : 0.3;
    this.cameras.main.flash(220, 255, 255, 255, false);
    if (finish === 'burst') {
      this.burstFx(loser);
      this.cameras.main.shake(500, 0.022);
    }

    const label =
      finish === 'burst' ? 'BURST FINISH!' : finish === 'ringout' ? 'RING-OUT!' : 'SPIN-OUT!';
    const winnerName = (playerWon ? this.player : this.opponent).def.name;
    this.hud.setStatus(`${label}\n${winnerName} takes the round`, '#ffd23b');

    const matchOver =
      this.playerScore >= this.winsNeeded || this.oppScore >= this.winsNeeded;

    this.time.delayedCall(1900, () => {
      this.matter.world.engine.timing.timeScale = 1; // restore before moving on
      if (matchOver) {
        this.scene.start('Results', {
          playerWon: this.playerScore >= this.winsNeeded,
          finish,
          playerId: this.match.playerId,
          opponentId: this.match.opponentId,
          playerScore: this.playerScore,
          oppScore: this.oppScore,
          mode: this.match.mode,
        });
      } else {
        this.nextRound();
      }
    });
  }

  /** Reset both beys and run the next round's countdown. */
  private nextRound(): void {
    this.roundNum++;
    const { cx, cy } = WORLD.arena;
    this.player.reset(cx - this.rim * 0.5, cy + this.rim * 0.3);
    this.opponent.reset(cx + this.rim * 0.5, cy - this.rim * 0.3);
    this.applyStartSpin();
    this.phase = 'countdown';
    this.runCountdown();
  }

  private applyStartSpin(): void {
    if (Number.isNaN(this.startSpin)) return;
    this.player.spin = this.startSpin;
    this.opponent.spin = this.startSpin;
  }

  private onCollision(event: Phaser.Physics.Matter.Events.CollisionStartEvent): void {
    if (this.phase !== 'fight') return;
    for (const pair of event.pairs) {
      const a = this.beyOf(pair.bodyA);
      const b = this.beyOf(pair.bodyB);
      if (!a || !b || a === b) continue;
      this.handleClash(a, b);
    }
  }

  private beyOf(body: MatterJS.BodyType): Bey | undefined {
    const go = (body as unknown as { gameObject?: Phaser.GameObjects.GameObject }).gameObject;
    return go?.getData('bey') as Bey | undefined;
  }

  private handleClash(a: Bey, b: Bey): void {
    const rel = sub(a.velocity, b.velocity);
    const impact = Math.min(20, len(rel) * Math.max(a.impactMultiplier, b.impactMultiplier));

    const sideOf = (bey: Bey): ClashSide => ({
      stats: bey.def.stats,
      type: bey.def.type,
      spin: bey.spin,
      burst: bey.burst,
      spinDir: bey.def.spinDir,
    });
    const out = resolveClash(sideOf(a), sideOf(b), impact);

    // Start from the raw outcome, then let Iron Counter reflect it: a bey with
    // an active counter takes no spin loss/knockback and slams it back.
    let lossA = out.spinLossA;
    let lossB = out.spinLossB;
    let kbA = out.knockbackA;
    let kbB = out.knockbackB;
    let aggressor = out.aggressor;
    if (a.counterActive) {
      lossB = Math.max(lossA, lossB) * 2.2;
      kbB = Math.max(kbA, kbB) * 2;
      lossA = 0;
      kbA = 0;
      aggressor = 'a';
      a.consumeCounter();
      this.impactFlash(a.pos.x, a.pos.y, 2.6, a.def.accent);
    }
    if (b.counterActive) {
      lossA = Math.max(lossA, lossB) * 2.2;
      kbA = Math.max(kbA, kbB) * 2;
      lossB = 0;
      kbB = 0;
      aggressor = 'b';
      b.consumeCounter();
      this.impactFlash(b.pos.x, b.pos.y, 2.6, b.def.accent);
    }

    a.applyClash(lossA, out.burstGainA);
    b.applyClash(lossB, out.burstGainB);
    // Charge specials from the exchange (dealt to foe + taken).
    a.chargeSpecial(lossB, lossA);
    b.chargeSpecial(lossA, lossB);

    // Knockback: shove each bey away from the other.
    const dirA = normalize(sub(a.pos, b.pos));
    a.addImpulse(scale(dirA, kbA * PHYS.knockbackScale));
    b.addImpulse(scale(dirA, -kbB * PHYS.knockbackScale));

    // Only the loser of a real (non-bump) clash becomes ring-out-able — so an
    // aggressor's own momentum never carries it out, but a solid hit can send
    // the bey it beats over the lip.
    if (impact >= PHYS.ringOut.minImpact) {
      const loser = aggressor === 'a' ? b : a;
      loser.markKnocked();
    }

    // FX — scaled to the size of the hit. Big hits get sparks, a flash, a
    // shake and hitstop; gentle center-stage bumps barely register.
    const mid = { x: (a.pos.x + b.pos.x) / 2, y: (a.pos.y + b.pos.y) / 2 };
    const n = out.bigHit ? Math.round(24 + impact * 1.5) : Math.max(3, Math.round(impact));
    this.sparks.emitParticleAt(mid.x, mid.y, n);
    this.cameras.main.shake(out.bigHit ? 200 : 60, Math.min(0.02, impact * 0.0009));
    if (impact >= 2 || out.bigHit) sound.clash(impact, out.bigHit);
    if (out.bigHit) {
      this.impactFlash(mid.x, mid.y, 1.6 + impact * 0.12, 0xffffff);
      this.cameras.main.flash(90, 255, 255, 255);
      this.hitstop();
    }
  }

  private hitstop(): void {
    const engine = this.matter.world.engine;
    engine.timing.timeScale = 0.12;
    this.time.delayedCall(70, () => {
      engine.timing.timeScale = 1;
    });
  }

  private applyChargeGlow(bey: Bey): void {
    // Pop the bey slightly while an extreme dash is charging/launching.
    const pop = bey.isCharging ? 1.18 : 1;
    bey.sprite.setScale(bey.baseScale * pop);
  }

  private burstFx(bey: Bey): void {
    this.sparks.emitParticleAt(bey.pos.x, bey.pos.y, 60);
    this.cameras.main.flash(200, 255, 230, 120);
    bey.sprite.setVisible(false);
  }
}
